// src/components/TZMMS-INV/dialogs/ScannerDialog.jsx
// ============================================================================
// TZMMS Inventory Counter — code scanner dialog
// ----------------------------------------------------------------------------
// PURPOSE
// - Contain camera scanning in a focused dialog opened by the + button.
// - Use @yudiel/react-qr-scanner for multi-scan phone-camera workflow.
// ============================================================================

import React from 'react';
import { Scanner } from '@yudiel/react-qr-scanner';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  QR_SCANNER_ALLOW_DUPLICATE_VALUES,
  QR_SCANNER_COMPONENTS,
  QR_SCANNER_CONSTRAINTS,
  QR_SCANNER_FORMATS,
  QR_SCANNER_SCAN_DELAY_MS,
} from '../constants/tzmmsInvUi.js';

export default function ScannerDialog({
  open,
  paused,
  errorMessage,
  lastScannedValue,
  onClose,
  onTogglePaused,
  onScan,
  onError,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          margin: { xs: 0, sm: 2 },
          maxHeight: { xs: '100vh', sm: 'auto' },
        },
      }}
    >
      {/* ===================================================================== */}
      {/* BLOCK TZI.SCAN.001 — Dialog title + instructions */}
      {/* ===================================================================== */}
      <DialogTitle>Scan Codes</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={{ xs: 1.5, md: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Point the rear camera at a QR code, Data Matrix, Aztec, EAN-13, or Code128 barcode. Each
            successful scan appends its text into the inventory list. On Samsung devices, use zoom
            and lighting for best focus.
          </Typography>

          {!!errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          {/* ================================================================= */}
          {/* BLOCK TZI.SCAN.002 — Camera scanner */}
          {/* ================================================================= */}
          <Box
            sx={{
              borderRadius: 2,
              overflow: 'hidden', // Keeps the embedded video inside rounded corners.
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Scanner
              onScan={onScan} // Parent hook handles duplicate protection + list insertion.
              onError={onError} // Bubble camera access errors back to the hook.
              paused={paused} // Allow user to pause without closing the dialog.
              formats={QR_SCANNER_FORMATS} // Restrict scanning to the supported 2D code formats.
              scanDelay={QR_SCANNER_SCAN_DELAY_MS} // Throttle successful detections.
              allowMultiple={QR_SCANNER_ALLOW_DUPLICATE_VALUES} // Session logic decides final duplicates.
              constraints={QR_SCANNER_CONSTRAINTS} // Prefer phone rear camera.
              components={QR_SCANNER_COMPONENTS} // Enable finder / torch / zoom / onOff controls.
              styles={{
                container: {
                  width: '100%',
                  minHeight: 'min(360px, 50vw)',
                  background: '#000',
                },
                video: {
                  objectFit: 'cover', // Fill the preview area cleanly.
                },
              }}
            />
          </Box>

          {/* ================================================================= */}
          {/* BLOCK TZI.SCAN.003 — Last scanned text */}
          {/* ================================================================= */}
          <Box>
            <Typography variant="body2" color="text.secondary">
              Last Accepted Scan
            </Typography>
            <Typography
              variant="body1"
              sx={{
                wordBreak: 'break-word',
                fontFamily: 'monospace',
              }}
            >
              {lastScannedValue || 'No accepted scans yet'}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      {/* ===================================================================== */}
      {/* BLOCK TZI.SCAN.004 — Dialog actions */}
      {/* ===================================================================== */}
      <DialogActions sx={{ gap: 1, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
        <Button onClick={onTogglePaused} fullWidth>
          {paused ? 'Resume Scan' : 'Pause Scan'}
        </Button>
        <Button variant="contained" onClick={onClose} fullWidth>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
